// Registers Stimulus controllers only once an element that uses them shows up in
// the DOM. Each controller keeps its own top level imports, so the bundler pulls
// its dependencies (gsap plugins, swiper, lottie, vidstack...) into the
// controller chunk instead of the entrypoint.
// More or less a simplified version of https://github.com/danieldiekmeier/stimulus-controller-resolver/blob/main/src/stimulus-controller-resolver.js
// On the homepage, this improve our lighthouse performance score by 7 since we no longer load
// page-specific JS where they aren't needed. (ie: flatpickr is heavy and only ever used in 2 pages)

const identifiersFor = (element) => (element.getAttribute("data-controller") ?? "").split(/\s+/).filter(Boolean);

export const lazyLoadControllers = (application, definitions) => {
  const pending = new Map(Object.entries(definitions));

  const load = (identifier) => {
    const loader = pending.get(identifier);
    if (!loader) return;

    pending.delete(identifier);

    loader()
      .then(({ default: controller }) => application.register(identifier, controller))
      .catch((error) => application.handleError(error, `Failed to load controller "${identifier}"`, { identifier }));
  };

  const loadWithin = (element) => {
    if (element.nodeType !== Node.ELEMENT_NODE) return;

    identifiersFor(element).forEach((id) => load(id));
    element.querySelectorAll("[data-controller]").forEach((child) => identifiersFor(child).forEach(load));
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") {
        loadWithin(mutation.target);
      } else {
        mutation.addedNodes.forEach(loadWithin);
      }

      if (pending.size === 0) break;
    }

    if (pending.size === 0) observer.disconnect();
  });

  loadWithin(document.documentElement);

  if (pending.size > 0) {
    observer.observe(document.documentElement, {
      attributeFilter: ["data-controller"],
      childList: true,
      subtree: true,
    });
  }
};
